/**
 * resolveUrlClassCharacter — the headless `?class=X` entry into the world.
 *
 * Extracted from QuickClassPickerScreen (deleted 2026-08-06) when the class-card
 * UI was retired in favour of the roster + CharacterCustomizer. The cards were
 * redundant with the creator; this effect was NOT. `useAuth.ts` calls it out:
 * the synthetic dev-spawn is gated on `isStrictLocalhost || !multiplayer`, so on
 * PTR / QA tunnels / production this is the only path that resolves a REAL
 * (server-persisted) character from a URL.
 *
 * Consumers of that contract: scripts/vps/verify-ptr.mjs, window-shot.mjs,
 * window-certify-probe.mjs, pre-room-freeze-round.ts, race-feel-analyzer.ts,
 * vqa-class-switch-probe.mjs, the pos-drift recorder, the MCP probe scenarios,
 * three e2e specs, and ClassSwitcherTopBar. Their URLs are unchanged by the
 * extraction — `quickPicker` is now simply ignored rather than required.
 */

import { ApiClient } from '../../../services/ApiClient';
import { DBCharacter } from '../../services/CharacterService';
import { CharacterClass } from '../../../shared/CharacterTypes';
import { gameLogger } from '../../../utils/GameLogger';
import {
  resolveToCharacterClass,
  formatCallingName,
} from '../../../shared/classes/CallingDisplayRegistry';
import { bootSearch } from '../../../config/boot/bootUrl';

/** The four-field starter look a URL-spawned hero gets. The creator owns the
 *  rich cosmetic path; this is deliberately minimal. */
const MIN_APPEARANCE = {
  bodyColor: '#d4a574',
  hairColor: '#3a2817',
  hairStyle: 'short',
  eyeColor: '#4a90e2',
};

function timestamp4(): string {
  return Date.now().toString().slice(-4);
}

function search(): string {
  try {
    return bootSearch();
  } catch {
    return '';
  }
}

/**
 * Read `?class=<name>` and resolve to a CharacterClass, or null if absent or
 * unmatched. Supports canonical class IDs, original calling names, and aliases.
 */
export function readUrlClass(): CharacterClass | null {
  try {
    const raw = new URLSearchParams(search()).get('class');
    if (!raw) return null;
    return resolveToCharacterClass(raw);
  } catch {
    /* SSR / malformed URL — no-op */
  }
  return null;
}

/**
 * Whether the auto-pick must stand down because another flow owns the join.
 *
 * Both rules are the 2026-07-09 quest-loss root fix, and both are load-bearing:
 *
 *  - explicit `pickCharacter=0` anywhere — the synthetic dev-spawn owns the
 *    join, and auto-provisioning in parallel swapped the join identity to a
 *    fresh roster character mid-session, orphaning every MemoryCharacterStore
 *    snapshot (quests / inventory / level).
 *  - strict localhost without an explicit `pickCharacter=1` — the localhost
 *    default IS auto-spawn, and portal navigation URLs drop the param, which
 *    used to let a carried `?class` mint a fresh identity mid-journey.
 */
export function isAutoPickSuppressed(): boolean {
  try {
    const sp = new URLSearchParams(search());
    const pick = sp.get('pickCharacter');
    const host = (globalThis as any).window?.location?.hostname;
    const strictLocalhost = host === 'localhost' || host === '127.0.0.1';
    return pick === '0' || (strictLocalhost && pick !== '1');
  } catch {
    return false; // malformed URL — proceed as before
  }
}

/**
 * Whether this URL will attempt an auto-pick — decidable SYNCHRONOUSLY, before
 * any network call.
 *
 * PreGameFlow needs this at first render: `resolveUrlClassCharacter` is async
 * (a list call, sometimes a create), and without a synchronous answer the
 * roster paints for a frame or two before the resolved character replaces it.
 * The retired QuickClassPickerScreen auto-picked "before painting, so nothing
 * flashes" — this keeps that property.
 */
export function willAutoPickFromUrl(): boolean {
  return readUrlClass() !== null && !isAutoPickSuppressed();
}

/**
 * Resolve the character named by `?class=X`, reusing the player's existing hero
 * of that class before minting a new one.
 *
 * REUSE BEFORE CREATE (quest-loss root fix, 2026-07-09): `?class=mage` means
 * "play as mage", not "mint another mage". Minting every visit orphaned all
 * persisted state under the previous character id — the hollow-coil e2e lost its
 * quest chain on every cross-map page navigation once a cached JWT routed load
 * 2+ through this path. A failing list call falls through to create, which is
 * the pre-fix behavior and still better than dead-ending.
 *
 * @returns the character to enter the world with, or null when no `?class=` is
 *          present or the auto-pick is suppressed.
 * @throws  whatever `createCharacter` throws — the caller surfaces it.
 */
export async function resolveUrlClassCharacter(
  sessionToken: string,
): Promise<DBCharacter | null> {
  const klass = readUrlClass();
  if (!klass) return null;

  if (isAutoPickSuppressed()) {
    gameLogger.info('character', '⚡ URL class auto-pick suppressed (synthetic flow owns the join)');
    return null;
  }

  gameLogger.info('character', `⚡ Auto-picking from URL ?class=${klass}`);

  try {
    const existing = (await ApiClient.fetchCharacters(sessionToken)) as DBCharacter[];
    const sameClass = (Array.isArray(existing) ? existing : []).filter(
      (c) => String(c.class ?? '').toLowerCase() === String(klass).toLowerCase(),
    );
    if (sameClass.length > 0) {
      const newest = sameClass.reduce((a, b) =>
        Number(new Date((b as any).lastPlayed ?? 0)) > Number(new Date((a as any).lastPlayed ?? 0)) ? b : a,
      );
      gameLogger.info('character', `⚡ Reusing existing ${klass} "${newest.name}" (${newest._id})`);
      return newest;
    }
  } catch (listErr: any) {
    gameLogger.warn('character', 'Reuse list failed — falling through to create:', listErr?.message);
  }

  const name = `Quick-${formatCallingName(klass)}-${timestamp4()}`;
  gameLogger.info('character', `⚡ Creating ${klass} as "${name}"`);
  const char = await ApiClient.createCharacter(sessionToken, {
    name,
    class: klass,
    appearance: MIN_APPEARANCE,
    pin: '000',
  });
  return char as DBCharacter;
}
